import React from 'react';
import { useCalculator } from '../../context/CalculatorContext';
import { formatCurrency, formatNumber } from '../../utils/calculations';

const ResultsSummary = () => {
  const { results } = useCalculator();

  if (!results) {
    return <div className="text-center py-8 text-gray-600">Loading results...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Main FTE Result */}
      <div className="bg-gradient-to-r from-slate-800 to-slate-600 rounded-lg shadow-lg p-8 text-white">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4">Total FTE Required</h2>
          <div className="text-5xl font-bold text-green-400 my-4">
            {results.totalFTEs.toFixed(2)}
          </div>
          
          {/* Current vs Required FTE */}
          {results.currentFTE && (
            <div className="mt-6 p-4 bg-yellow-100 rounded-lg border border-yellow-300">
              <div className="flex justify-between items-center mb-2">
                <span className="text-yellow-800 font-medium">Current FTEs:</span>
                <span className="text-yellow-900 font-bold text-xl">{results.currentFTE}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-red-800 font-medium">Deficit:</span>
                <span className="text-red-900 font-bold text-xl">
                  {(results.totalFTEs - results.currentFTE).toFixed(1)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Cost Summary */}
        <div className="mt-8 pt-6 border-t border-gray-600">
          <h3 className="font-semibold text-lg mb-4">💰 Cost Summary</h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-300">Base Salary:</span>
              <span className="font-semibold">{formatCurrency(results.baseSalaryCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-300">Benefits ({results.benefitsPercent}%):</span>
              <span className="font-semibold">{formatCurrency(results.benefitsCost)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold border-t border-gray-600 pt-2">
              <span>Total Annual Cost:</span>
              <span className="text-green-400">{formatCurrency(results.totalCost)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Defensibility Breakdown */}
      <div className="bg-white rounded-lg shadow-md p-8">
        <h3 className="font-semibold text-lg text-gray-900 mb-6">📊 Defensibility Breakdown</h3>
        
        <div className="space-y-6">
          {/* H_FTE */}
          <div className="border-b pb-4 mb-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700 font-medium">
                Productive Hours per FTE (H<sub>FTE</sub>)
              </span>
              <span className="text-green-700 font-bold text-lg">
                {formatNumber(results.productiveHoursFTE)}
              </span>
            </div>
            <div className="text-sm text-gray-600 ml-4 mt-2">
              Calculation: ({results.totalHours} - {results.leaveHours}) × (1 - {(results.adminPercent / 100).toFixed(2)})
            </div>
          </div>

          {/* H_Total */}
          <div className="border-b pb-4 mb-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700 font-medium">
                Total Annual Workload (H<sub>Total</sub>)
              </span>
              <span className="text-green-700 font-bold text-lg">
                {formatNumber(results.totalWorkloadHours)}
              </span>
            </div>
            <div className="space-y-2 text-sm ml-4 mt-3">
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Core Program Hours (H<sub>Core</sub>):
                </span>
                <span className="font-medium">{formatNumber(results.coreHours)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Activity-Based Hours (H<sub>Activity</sub>):
                </span>
                <span className="font-medium">{formatNumber(results.scaledActivityHours)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  Logistical Hours (H<sub>Logistics</sub>):
                </span>
                <span className="font-medium">{formatNumber(results.logisticsHours)}</span>
              </div>
              {results.fireSafetyHours > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    🔥 Fire Safety Hours (H<sub>Fire</sub>):
                  </span>
                  <span className="font-medium text-orange-700">{formatNumber(results.fireSafetyHours)}</span>
                </div>
              )}
              {results.environmentalHours > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">
                    🌱 Environmental Hours (H<sub>Env</sub>):
                  </span>
                  <span className="font-medium text-green-700">{formatNumber(results.environmentalHours)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Final Calculation */}
          <div className="pt-2">
            <div className="flex justify-between items-center mb-2">
              <span className="text-gray-700 font-medium">Final FTE Calculation</span>
              <span className="text-green-700 font-bold text-lg">
                {results.totalFTEs.toFixed(2)}
              </span>
            </div>
            <div className="text-sm text-gray-600 ml-4 mt-2">
              Calculation: {formatNumber(results.totalWorkloadHours)} / {formatNumber(results.productiveHoursFTE)}
            </div>
          </div>
        </div>
      </div>

      {/* Benchmark Comparison - Only for hospitals */}
      {results.benchmarkFTE > 0 && (
        <div className="bg-white rounded-lg shadow-md p-8">
          <h3 className="font-semibold text-lg text-gray-900 mb-6">🎯 Industry Benchmark Comparison</h3>
          <div className="space-y-2">
            {/* Your Calculated FTE */}
            <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 flex justify-between items-center">
              <div className="text-sm font-semibold text-gray-700">
                Your Calculated FTE
              </div>
              <div className="text-lg font-bold text-blue-600">
                {results.totalFTEs.toFixed(2)}
              </div>
            </div>
            
            {/* Industry Benchmark */}
            <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
              <div className="flex justify-between items-center">
                <div className="text-sm font-semibold text-gray-700">
                  Industry Benchmark
                </div>
                <div className="text-lg font-bold text-purple-600">
                  {results.benchmarkFTE.toFixed(2)}
                </div>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Based on population ratios
              </div>
            </div>
            
            {/* Variance */}
            <div className={`p-3 rounded-lg border ${
              results.benchmarkStatus === 'good' ? 'bg-green-50 border-green-200' :
              results.benchmarkStatus === 'warning' ? 'bg-yellow-50 border-yellow-200' :
              'bg-red-50 border-red-200'
            }`}>
              <div className="flex justify-between items-center">
                <div className="text-sm font-semibold text-gray-700">
                  Variance
                </div>
                <div className={`text-lg font-bold ${
                  results.benchmarkStatus === 'good' ? 'text-green-700' :
                  results.benchmarkStatus === 'warning' ? 'text-yellow-600' :
                  'text-red-700'
                }`}>
                  {(results.benchmarkVariance * 100).toFixed(1)}%
                </div>
              </div>
              <div className="text-xs text-gray-600 mt-1 font-medium">
                {results.benchmarkStatus === 'good' && '✓ Within industry norms'}
                {results.benchmarkStatus === 'warning' && '⚠️ Review recommended'}
                {results.benchmarkStatus === 'alert' && '⚠️ Significant variance'}
              </div>
            </div>
          </div>
          
          <div className="mt-6 p-5 bg-blue-50 rounded-lg border border-blue-200 text-sm text-gray-700 leading-relaxed">
            <strong>💡 Benchmark Methodology:</strong> Based on industry standards of 1 FTE per 500-800 employees,
            1 FTE per 400 hospital beds, and facility size factors.
          </div>
        </div>
      )}

      {/* Efficiency Metrics */}
      <div className="bg-white rounded-lg shadow-md p-8">
        <h3 className="font-semibold text-lg text-gray-900 mb-6">📈 Efficiency Metrics</h3>
        <div className="space-y-2">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200 flex justify-between items-center">
            <div className="text-sm font-semibold text-gray-700">
              Hours per Product
            </div>
            <div className="text-lg font-bold text-blue-600">
              {formatNumber(results.hoursPerProduct)} hrs
            </div>
          </div>

          <div className="bg-green-50 p-3 rounded-lg border border-green-200 flex justify-between items-center">
            <div className="text-sm font-semibold text-gray-700">
              FTE Utilization
            </div>
            <div className="text-lg font-bold text-green-700">
              {results.utilization.toFixed(1)}%
            </div>
          </div>

          <div className="bg-purple-50 p-3 rounded-lg border border-purple-200 flex justify-between items-center">
            <div className="text-sm font-semibold text-gray-700">
              Cost per Product
            </div>
            <div className="text-lg font-bold text-purple-600">
              {formatCurrency(results.costPerProduct)}
            </div>
          </div>

          <div className="bg-orange-50 p-3 rounded-lg border border-orange-200 flex justify-between items-center">
            <div className="text-sm font-semibold text-gray-700">
              Average Risk Level
            </div>
            <div className="text-lg font-bold text-orange-700">
              {results.avgRisk.toFixed(1)}%
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsSummary;
